import React, { useEffect, useRef, useState } from "react";
import { useMatch, useNavigate } from "react-router";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useShallow } from "zustand/react/shallow";
import { AppearancePicker } from "components/common/AppearancePicker";
import { Tooltip } from "components/common/Tooltip";
import { showNotificationGlobal } from "stores/useNotificationStore";
import { resolveDatasetColor } from "@/lib/datasetColor";
import { SHELL_PAGES } from "@/lib/navigation/pages";
import type { Space } from "@/lib/api/organizations";
import {
  SPACE_PALETTE,
  spaceAncestors,
  spaceColor,
  useOrgContextStore,
} from "@/components/main/stores/orgContextStore";
import {
  NamingOverlay,
  type NamingOverlayHandle,
} from "@/components/main/utils/NamingOverlay";
import {
  attemptShown,
  nameLength,
  overLimitMessage,
  retryMessage,
  type NamingMessage,
  type NamingPhase,
} from "@/components/main/utils/naming";
import { SpaceDot } from "./SpaceDot";
import { MAX_SPACE_NAME_CHARS, MIN_SPACE_NAME_CHARS } from "./spaceName";

interface NewSpaceOverlayProps {
  open: boolean;
  /// Fixed by the "+" that opened it: none founds a top-level space, a space's
  /// "+" creates inside that space.
  parentId: string | null;
  onClose: () => void;
}

type RootKind = "restricted" | "sealed";

const KIND_LABEL: Record<RootKind, string> = {
  restricted: "Restricted",
  sealed: "Sealed",
};

const KIND_COPY: Record<RootKind, string> = {
  restricted:
    "Only the people you add can see it and open it. What's inside can be shared and exported as usual.",
  sealed:
    "For confidential work. Only the people you add know it exists, and nothing inside can leave it: no exporting, no sharing outside the space, and no access for AI assistants.",
};

const mainPane = () => document.querySelector<HTMLElement>("[data-main-pane]");

// Resolved when read: the rail's "+" lives outside the page it centres on.
const PANE_REF: React.RefObject<HTMLElement | null> = {
  get current() {
    return mainPane();
  },
};
const ANCHOR_REF: React.RefObject<HTMLElement | null> = {
  get current() {
    const pane = mainPane();
    return pane?.querySelector<HTMLElement>("[data-naming-anchor]") ?? pane;
  },
};

const spaceRow = (spaceId: string): HTMLElement | null =>
  Array.from(document.querySelectorAll<HTMLElement>("[data-space-id]")).find(
    (el) => el.dataset.spaceId === spaceId,
  ) ?? null;

const settleRow = (row: HTMLElement) => {
  row.classList.add("naming-landed");
  row.addEventListener(
    "animationend",
    () => row.classList.remove("naming-landed"),
    { once: true },
  );
};

/// No id exists to derive a color from yet, so the swatch shown is the one sent.
const anyPaletteColor = (): string =>
  SPACE_PALETTE[Math.floor(Math.random() * SPACE_PALETTE.length)];

const tip = (title: string, body: string) => (
  <>
    <span className="naming-tip-title">{title}</span>
    {body}
  </>
);

/// D50: the "+" decided where, and where decides what. Inside a space the new
/// space is Open; at the top level the one choice left is how closed it is.
export const NewSpaceOverlay: React.FC<NewSpaceOverlayProps> = ({
  open,
  parentId,
  onClose,
}) => {
  const overlayRef = useRef<NamingOverlayHandle>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const glyphRef = useRef<HTMLButtonElement>(null);
  const kindRefs = useRef<Record<RootKind, HTMLButtonElement | null>>({
    restricted: null,
    sealed: null,
  });

  const [name, setName] = useState("");
  const [color, setColor] = useState(anyPaletteColor);
  const [kind, setKind] = useState<RootKind>("restricted");
  const [colorOpen, setColorOpen] = useState(false);
  const [phase, setPhase] = useState<NamingPhase>("idle");
  const [refusal, setRefusal] = useState<string | null | undefined>();

  const navigate = useNavigate();
  const onOverview = useMatch(SHELL_PAGES.Overview.path) !== null;
  const { spaces, setSelectedSpace, createSpace, orgName } = useOrgContextStore(
    useShallow((s) => ({
      spaces: s.spaces,
      setSelectedSpace: s.setSelectedSpace,
      createSpace: s.createSpace,
      orgName: s.activeOrg()?.name,
    })),
  );

  useEffect(() => {
    if (!open) return;
    setName("");
    setColor(anyPaletteColor());
    setKind("restricted");
    setColorOpen(false);
    setPhase("idle");
    setRefusal(undefined);
  }, [open]);

  const parent = parentId
    ? (spaces.find((s) => s.space_id === parentId) ?? null)
    : null;
  // A sealed root walls every Open space under it, however deep (D40).
  const wall = parent
    ? ([...spaceAncestors(spaces, parent.space_id), parent].find(
        (s) => s.classification === "sealed",
      ) ?? null)
    : null;

  const trimmed = name.trim();
  const length = nameLength(name);
  const over = length > MAX_SPACE_NAME_CHARS;
  // A parent that is gone must not quietly turn the create into a root.
  const canCreate = length >= MIN_SPACE_NAME_CHARS && (!parentId || !!parent);
  const busy = phase !== "idle";

  const duplicate =
    trimmed.length > 0 &&
    spaces.some(
      (s) =>
        (s.parent_space_id ?? null) === parentId &&
        !s.deletion_scheduled_at &&
        s.name.trim().toLowerCase() === trimmed.toLowerCase(),
    );

  const message: NamingMessage | null =
    refusal !== undefined
      ? refusal === null
        ? retryMessage("space")
        : { kind: "error", content: refusal }
      : over
        ? overLimitMessage(length, MAX_SPACE_NAME_CHARS)
        : duplicate
          ? {
              kind: "warn",
              content: (
                <>
                  {parent ? <b>{parent.name}</b> : (orgName ?? "This org")}{" "}
                  already has a space named <b>{trimmed}</b>
                </>
              ),
            }
          : null;

  const cancel = async () => {
    if (busy) return;
    setPhase("leaving");
    await overlayRef.current?.leave();
    onClose();
  };

  const submit = async () => {
    if (busy || !canCreate || over) return;
    setColorOpen(false);
    setRefusal(undefined);
    setPhase("creating");
    const startedAt = performance.now();
    let created: Space | null = null;
    let failure: string | null = null;
    try {
      // Nobody named ⇒ the door makes the founder the first manager, sealed
      // included; further managers are granted in the space's settings.
      created = await createSpace(trimmed, {
        parentSpaceId: parent?.space_id,
        color,
        classification: parent ? "open" : kind,
      });
    } catch (e) {
      failure = e instanceof Error ? e.message : null;
    }
    if (!created) {
      await attemptShown(startedAt);
      setPhase("idle");
      setRefusal(failure);
      inputRef.current?.focus();
      return;
    }
    setPhase("leaving");
    const { space_id: spaceId, name: createdName } = created;
    // Selected as a click on its row would select it, so the next model goes
    // in it; selecting also unfolds the parents it lands under.
    setSelectedSpace(spaceId);
    if (!onOverview) navigate(SHELL_PAGES.Overview.path);
    const landed = await overlayRef.current?.landIn(
      () => spaceRow(spaceId),
      settleRow,
    );
    // A folded or closed rail shows no row, so the create says so instead.
    if (!landed) {
      showNotificationGlobal(`Space "${createdName}" created`, "success");
    }
    onClose();
  };

  const pickKind = (next: RootKind) => {
    if (!busy) setKind(next);
  };

  const footStart = parent ? (
    <Tooltip
      placement="bottom-start"
      maxWidth="17rem"
      title={tip(
        "Open",
        wall
          ? `${wall.name} is sealed, so this space is too: its data can move anywhere inside ${wall.name}, but it can't be exported or shared outside it.`
          : `Everyone who can open ${parent.name} can open this space too.`,
      )}
    >
      <span className="naming-target" tabIndex={0}>
        <SpaceDot color={spaceColor(parent)} />
        <span className="naming-target-name">{parent.name}</span>
        {wall && (
          <LockOutlinedIcon className="naming-lock" sx={{ fontSize: 11 }} />
        )}
      </span>
    </Tooltip>
  ) : (
    <span role="radiogroup" aria-label="Kind of space" className="naming-kind">
      {(["restricted", "sealed"] as const).map((k) => (
        <Tooltip
          key={k}
          placement="bottom-start"
          maxWidth="17rem"
          title={tip(KIND_LABEL[k], KIND_COPY[k])}
        >
          <button
            ref={(el) => {
              kindRefs.current[k] = el;
            }}
            type="button"
            role="radio"
            aria-checked={kind === k}
            tabIndex={kind === k ? 0 : -1}
            // A click leaves the caret in the name, so ↵ still creates.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => pickKind(k)}
            onKeyDown={(e) => {
              if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
              e.preventDefault();
              const next = kind === "sealed" ? "restricted" : "sealed";
              pickKind(next);
              kindRefs.current[next]?.focus();
            }}
          >
            {k === "sealed" && <LockOutlinedIcon sx={{ fontSize: 11 }} />}
            {KIND_LABEL[k]}
          </button>
        </Tooltip>
      ))}
    </span>
  );

  return (
    <>
      <NamingOverlay
        ref={overlayRef}
        open={open}
        ariaLabel={parent ? `New space inside ${parent.name}` : "New space"}
        anchorRef={ANCHOR_REF}
        paneRef={PANE_REF}
        inputRef={inputRef}
        placeholder="Space name"
        name={name}
        onNameChange={(next) => {
          setName(next);
          setRefusal(undefined);
        }}
        maxChars={MAX_SPACE_NAME_CHARS}
        message={message}
        phase={phase}
        canCreate={canCreate}
        glyph={<SpaceDot color={color} size="lg" />}
        glyphLabel="Color"
        glyphRef={glyphRef}
        glyphExpanded={colorOpen}
        onGlyphClick={() => setColorOpen((isOpen) => !isOpen)}
        footStart={footStart}
        onSubmit={() => void submit()}
        onCancel={() => void cancel()}
      />
      <AppearancePicker
        isOpen={open && colorOpen}
        onClose={() => {
          setColorOpen(false);
          inputRef.current?.focus();
        }}
        anchorRef={glyphRef}
        currentColor={color}
        colors={SPACE_PALETTE}
        resolveColor={resolveDatasetColor}
        onColorChange={(picked) => {
          setColor(picked);
          setColorOpen(false);
          inputRef.current?.focus();
        }}
      />
    </>
  );
};
